import { cx } from '@emotion/css';
import { useNavigationPreviousPage } from '@/hooks/useNavigationPreviousPage';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import { useEffect, useRef, useState } from 'react';
import { useLiveFeedback } from '@/hooks';
import { LiveFeedback } from '@/components';
import styles from './OrgProfileHeader.styles';

interface OrgProfileHeaderProps {
  image: string;
  isImageLoading?: boolean;
  trackButtonClick: (label: string) => void;
  toggleDrawer: (open: boolean) => (event: any) => void;
  orgName: string;
}

const OrgProfileHeader: React.FC<OrgProfileHeaderProps> = ({
  image,
  isImageLoading = false,
  trackButtonClick,
  toggleDrawer,
  orgName,
}) => {
  const navigatePreviousPage = useNavigationPreviousPage();
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setIsImageLoaded(!!imgRef.current?.complete && imgRef.current.naturalWidth > 0);
  }, [image]);

  const showSkeleton = isImageLoading || (!!image && !isImageLoaded);

  const goToPreviousPage = () => {
    trackButtonClick('Back button');
    navigatePreviousPage();
  };

  const handleClickShare = async () => {
    try {
      trackButtonClick('Share Organization');
      await navigator.share({ title: orgName, text: '', url: window.location.href });
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setFeedbackMessage('Unable to share. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container} aria-busy={showSkeleton}>
      <LiveFeedback message={feedbackMessage} />
      <Skeleton
        variant="rectangular"
        animation="wave"
        aria-hidden="true"
        width="100%"
        height="100%"
        className={cx(styles.imageLayer, showSkeleton ? styles.layerVisible : styles.layerHidden)}
      />
      {image && (
        <img
          ref={imgRef}
          src={image}
          alt=""
          className={cx(
            styles.imageLayer,
            styles.image,
            isImageLoaded ? styles.layerVisible : styles.layerHidden
          )}
          onLoad={() => setIsImageLoaded(true)}
        />
      )}
      <div className={styles.buttons}>
        <IconButton className={styles.button} onClick={goToPreviousPage} aria-label="Back button">
          <KeyboardArrowLeftIcon />
        </IconButton>
        <div>
          {typeof navigator.share === 'function' && (
            <IconButton
              className={cx(styles.button, styles.buttonSpacing)}
              aria-label="Share organization page"
              onClick={handleClickShare}
            >
              <IosShareOutlinedIcon />
            </IconButton>
          )}

          <IconButton
            className={styles.button}
            aria-label="More options"
            onClick={toggleDrawer(true)}
          >
            <MoreHorizOutlinedIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default OrgProfileHeader;
